import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { DownloadIcon, YaySmallIcon } from 'secondstep-components'
import NavigationHelper from 'layers/navigation/navigationHelper'
import NavigatorButton from 'components/admin/PlaybookSteps/NavigatorButton'
import {
  EstimatedTime,
  GetStartedButton,
  Image,
  PageContent,
  PageNavigator,
  PlaybookStepsWrapper,
  RightContent,
  StepDropdown,
  StepNumber,
  StyledHr,
  StyledLinkDropdown,
  SubTitle,
  SummaryLink,
  TextContent,
  Title,
  TopWrapper,
} from './component.styles'
import {
  DROPDOWN_DEFAULT_LABEL,
  DROPDOWN_NAME,
  GET_STARTED_BUTTON_TEXT,
  GET_STARTED_BUTTON_TEST_ID,
  GOALS_TEXT,
  HOST_TEXT,
  MARK_COMPLETE_BUTTON_TEXT,
  PAGE_CONTENT_TEST_ID,
  PURPOSE_TEXT,
  RESUME_BUTTON_TEXT,
  SUMMARY_TEXT,
} from './constants'
import { dropdownOptions, modalMap, stepDataMap } from './data'

const PlaybookSteps = ({
  adminSetK8PlaybookStepsDropdown,
  familyLetterSent,
  goalsAreCompleted,
  goalsIsInDraft,
  implementationApproachIsCompleted,
  kickoffHosted,
  kickoffPrepared,
  linkDropdownDisplayTitle,
  linkMenu,
  navigationEvent,
  panelData,
  programStartDateIsCompleted,
  purposeIsCompleted,
  purposeIsInDraft,
  siteId,
  stateDropdownOption,
  userInvitationMethod,
}) => {
  const [activePanel, setActivePanel] = useState(null)
  const [activeStep, setActiveStep] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [ModalComponent, setModalComponent] = useState(null)
  const [selectedDropdownOption, setSelectedDropdownOption] = useState(
    stateDropdownOption || dropdownOptions[0],
  )
  const [showSummary, setShowSummary] = useState(false)

  const defaultLabel = selectedDropdownOption
    ? selectedDropdownOption.name
    : DROPDOWN_DEFAULT_LABEL

  const dropdownLinkNavigation = link => {
    const { download = null, openSameTab, url: optionUrlPath } = link || {}

    if (download) {
      navigationEvent(
        download?.file?.file?.url,
        NavigationHelper.types.DOWNLOAD_LINK,
      )
    }

    const type = openSameTab
      ? NavigationHelper.types.PUSH
      : NavigationHelper.types.NEW_TAB
    navigationEvent(optionUrlPath, type)
  }

  const handleActiveStep = () => {
    const stepConditions = {
      plan: [
        () => !implementationApproachIsCompleted,
        () => !purposeIsCompleted,
        () => !goalsAreCompleted,
        () => !programStartDateIsCompleted,
      ],
      launch: [
        () => !userInvitationMethod,
        () => !kickoffPrepared,
        () => !kickoffHosted,
        () => !familyLetterSent,
      ],
    }

    const conditions = stepConditions[selectedDropdownOption?.key] || []
    const newStep = conditions.findIndex(condition => condition())
    setActiveStep(newStep > 0 ? newStep : 0)
  }

  const launchModal = modalName => {
    const Modal = modalMap[modalName]?.component
    if (Modal) {
      setModalComponent(() => Modal)
      setIsModalOpen(true)
    }
  }

  const onChangeDropdown = e => {
    const optionToSelect = dropdownOptions.find(option => option.key === e.key)
    setSelectedDropdownOption(optionToSelect)
    adminSetK8PlaybookStepsDropdown({ [siteId]: optionToSelect })
    handleActiveStep()
  }

  const setButtonText = modalName => {
    let buttonText = GET_STARTED_BUTTON_TEXT

    if (modalName === GOALS_TEXT && goalsIsInDraft) {
      buttonText = RESUME_BUTTON_TEXT
    }
    if (modalName === PURPOSE_TEXT && purposeIsInDraft) {
      buttonText = RESUME_BUTTON_TEXT
    }
    if (modalName === HOST_TEXT) {
      buttonText = MARK_COMPLETE_BUTTON_TEXT
    }

    return buttonText
  }

  useEffect(() => {
    if (panelData?.length > 0) {
      const panel = panelData.find(({ header }) =>
        header.toLowerCase().includes(selectedDropdownOption?.key),
      )
      setActivePanel(panel)
    }
  }, [panelData, selectedDropdownOption])

  useEffect(() => {
    if (
      selectedDropdownOption?.key === 'plan' &&
      goalsAreCompleted &&
      implementationApproachIsCompleted &&
      programStartDateIsCompleted &&
      purposeIsCompleted
    ) {
      setShowSummary(true)
      setActiveStep(4)
    } else if (
      selectedDropdownOption?.key === 'launch' &&
      familyLetterSent &&
      kickoffPrepared &&
      kickoffHosted &&
      userInvitationMethod
    ) {
      setShowSummary(true)
      setActiveStep(4)
    } else {
      setShowSummary(false)
      handleActiveStep()
    }
  }, [
    activeStep,
    familyLetterSent,
    goalsAreCompleted,
    implementationApproachIsCompleted,
    kickoffHosted,
    kickoffPrepared,
    programStartDateIsCompleted,
    purposeIsCompleted,
    selectedDropdownOption,
    showSummary,
    userInvitationMethod,
  ])

  return (
    <PlaybookStepsWrapper>
      <TopWrapper>
        <StepDropdown
          dataTestId="playbook-steps-dropdown"
          defaultLabel={defaultLabel}
          keyProp="key"
          labelProp="name"
          name={DROPDOWN_NAME}
          onChange={onChangeDropdown}
          options={dropdownOptions}
          selectOption={selectedDropdownOption}
          value={selectedDropdownOption}
        />
      </TopWrapper>
      <PageContent dataTestId={PAGE_CONTENT_TEST_ID}>
        {!showSummary && (
          <Fragment key="not-complete-step">
            {activePanel?.modalFlows[activeStep]?.image?.file?.url && (
              <Image
                alt={activePanel?.modalFlows[activeStep]?.image?.title}
                src={activePanel?.modalFlows[activeStep]?.image?.file?.url}
              />
            )}
            <TextContent>
              <StepNumber>
                {activePanel?.modalFlows[activeStep]?.subhead}
              </StepNumber>
              <Title>{activePanel?.modalFlows[activeStep]?.shortHeader}</Title>
              <EstimatedTime>
                {activePanel?.modalFlows[activeStep]?.duration}
              </EstimatedTime>
            </TextContent>
            <GetStartedButton
              dataTestId={GET_STARTED_BUTTON_TEST_ID}
              onClick={() =>
                launchModal(
                  stepDataMap?.[selectedDropdownOption?.key]?.[activeStep]
                    ?.modalName,
                )
              }
            >
              {setButtonText(
                stepDataMap?.[selectedDropdownOption?.key]?.[activeStep]
                  ?.modalName,
              )}
            </GetStartedButton>
          </Fragment>
        )}
        {showSummary && selectedDropdownOption?.key === 'plan' && (
          <Fragment key="complete-step">
            <YaySmallIcon id="step-icon" />
            <TextContent>
              <Title>You're on your way!</Title>
              <SubTitle>
                Download and complete the customizable action plan and prepare
                for launch.
              </SubTitle>
            </TextContent>
            <RightContent>
              {linkMenu && (
                <StyledLinkDropdown
                  className="download-action-plan-button"
                  defaultLabel={linkDropdownDisplayTitle}
                  dropdownButtonIcon={<DownloadIcon />}
                  key="link-dropdown"
                  keyProp="id"
                  labelProp="buttonText"
                  onChange={link => dropdownLinkNavigation(link)}
                  options={linkMenu}
                  shouldHideSelectedIcon={true}
                />
              )}
              <SummaryLink onClick={() => launchModal(SUMMARY_TEXT)}>
                {SUMMARY_TEXT}
              </SummaryLink>
            </RightContent>
          </Fragment>
        )}
        {showSummary && selectedDropdownOption?.key === 'launch' && (
          <Fragment key="complete-step">
            <YaySmallIcon id="step-icon" />
            <TextContent>
              <Title>You've launched the program!</Title>
              <SubTitle>
                Use the Launch Snapshot to make sure teachers are getting
                started.
              </SubTitle>
            </TextContent>
          </Fragment>
        )}
      </PageContent>
      <PageNavigator>
        <NavigatorButton
          isActive={activeStep === 0}
          isCompleted={
            selectedDropdownOption?.key === 'plan'
              ? implementationApproachIsCompleted
              : !!userInvitationMethod
          }
          label={stepDataMap?.[selectedDropdownOption?.key]?.[0]?.label}
          onClick={() =>
            launchModal(
              stepDataMap?.[selectedDropdownOption?.key]?.[0]?.modalName,
            )
          }
        >
          {stepDataMap?.[selectedDropdownOption?.key]?.[0]?.label}
        </NavigatorButton>
        <StyledHr />
        <NavigatorButton
          isActive={activeStep === 1}
          isCompleted={
            selectedDropdownOption?.key === 'plan'
              ? purposeIsCompleted
              : !!kickoffPrepared
          }
          label={stepDataMap?.[selectedDropdownOption?.key]?.[1]?.label}
          onClick={() =>
            launchModal(
              stepDataMap?.[selectedDropdownOption?.key]?.[1]?.modalName,
            )
          }
        >
          {stepDataMap?.[selectedDropdownOption?.key]?.[1]?.label}
        </NavigatorButton>
        <StyledHr />
        <NavigatorButton
          isActive={activeStep === 2}
          isCompleted={
            selectedDropdownOption?.key === 'plan'
              ? goalsAreCompleted
              : !!kickoffHosted
          }
          label={stepDataMap?.[selectedDropdownOption?.key]?.[2]?.label}
          onClick={() =>
            launchModal(
              stepDataMap?.[selectedDropdownOption?.key]?.[2]?.modalName,
            )
          }
        >
          {stepDataMap?.[selectedDropdownOption?.key]?.[2]?.label}
        </NavigatorButton>
        <StyledHr />
        <NavigatorButton
          isActive={activeStep === 3}
          isCompleted={
            selectedDropdownOption?.key === 'plan'
              ? programStartDateIsCompleted
              : !!familyLetterSent
          }
          label={stepDataMap?.[selectedDropdownOption?.key]?.[3]?.label}
          onClick={() =>
            launchModal(
              stepDataMap?.[selectedDropdownOption?.key]?.[3]?.modalName,
            )
          }
        >
          {stepDataMap?.[selectedDropdownOption?.key]?.[3]?.label}
        </NavigatorButton>
      </PageNavigator>
      {isModalOpen && ModalComponent && (
        <ModalComponent
          handleClose={() => setIsModalOpen(false)}
          siteId={siteId}
        />
      )}
    </PlaybookStepsWrapper>
  )
}

PlaybookSteps.propTypes = {
  adminSetK8PlaybookStepsDropdown: PropTypes.func,
  familyLetterSent: PropTypes.bool,
  goalsAreCompleted: PropTypes.bool,
  goalsIsInDraft: PropTypes.bool,
  implementationApproachIsCompleted: PropTypes.bool,
  kickoffHosted: PropTypes.bool,
  kickoffPrepared: PropTypes.bool,
  linkDropdownDisplayTitle: PropTypes.string,
  linkMenu: PropTypes.array,
  navigationEvent: PropTypes.func,
  panelData: PropTypes.array,
  programStartDateIsCompleted: PropTypes.bool,
  purposeIsCompleted: PropTypes.bool,
  purposeIsInDraft: PropTypes.bool,
  siteId: PropTypes.number,
  stateDropdownOption: PropTypes.object,
  userInvitationMethod: PropTypes.string,
}

export default PlaybookSteps
